import React, { useEffect, useMemo, useState } from "react";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid2, Grid4, TableChip, BtnRow, BtnPrimary, BtnSecondary,
  BtnLight, InputLabel, FormInput, FormSelect, FormTextarea, HelpText, EmptyState, TableWrap, Th, Td,
} from "../components/ui";

const PRIORITY_OPTIONS = [
  "Academic Performance","Behavioural Development","Attendance","Social Skills",
  "Emotional Wellbeing","Communication Skills","Confidence Building","Discipline",
  "Learning Gap","Career Awareness","Leadership Development","Custom",
];
const EXECUTION_OPTIONS = [
  "Immediate Intervention","Routine Support","Targeted Intervention",
  "Counselling Support","Parent Involvement","Remedial Support","Monitoring-Based",
];
const FREQUENCY_OPTIONS = ["Daily","Twice a Week","Weekly","Fortnightly","Monthly","Need-Based"];
const STATUS_OPTIONS = ["Not Started","In Progress","On Track","Delayed","Completed"];

export default function Module9StudentDevelopment() {
  const {
    module9DevPlans,
    setModule9DevPlans,
    getAllStaff,
    module9Loading,
    module9LoadError,
    canEditModules1to5,
    loadModule9StudentDevelopmentPlan,
    saveModule9StudentDevelopmentPlan,
    resetModule9StudentDevelopmentPlan,
  } = useACPP();

  const staffList = useMemo(() => getAllStaff(), [getAllStaff]);

  const [form, setForm] = useState({
    priorityArea: "", customPriority: "", executionType: "", responsible: "",
    frequency: "", timeline: "", status: "", deadline: "", reviewDate: "",
    problemIdentified: "", rootCause: "", actionToBeTaken: "", successMetric: "",
  });
  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const [filterPriority, setFilterPriority] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState([]);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [moduleStatus, setModuleStatus] = useState("Pending");

  const signature = useMemo(() => (rows) => JSON.stringify(rows || []), []);

  useEffect(() => {
    if (!module9Loading) {
      setSavedSnapshot(module9DevPlans || []);
    }
  }, [module9Loading, module9DevPlans]);

  useEffect(() => {
    const current = module9DevPlans || [];
    if (!current.length && !savedSnapshot.length) {
      setModuleStatus("Pending");
      return;
    }
    if (signature(current) === signature(savedSnapshot)) {
      setModuleStatus("Saved");
      return;
    }
    setModuleStatus("In Progress");
  }, [module9DevPlans, savedSnapshot, signature]);

  const getFinalPriority = () => form.priorityArea === "Custom" ? form.customPriority : (form.priorityArea || form.customPriority);

  // ── Filters ──────────────────────────────────────────────────────────────
  const filterPriorities = [...new Set(module9DevPlans.map(i => i.priorityArea).filter(Boolean))];
  const filterStatuses = [...new Set(module9DevPlans.map(i => i.status).filter(Boolean))];
  const filtered = useMemo(() => module9DevPlans.filter(i =>
    (!filterPriority || i.priorityArea === filterPriority) && (!filterStatus || i.status === filterStatus)
  ), [module9DevPlans, filterPriority, filterStatus]);

  // ── Metrics ──────────────────────────────────────────────────────────────
  const priorityCount = [...new Set(module9DevPlans.map(i => i.priorityArea).filter(Boolean))].length;
  const openCount = module9DevPlans.filter(i => i.status !== "Completed").length;
  const completedCount = module9DevPlans.filter(i => i.status === "Completed").length;

  // ── Leader summary grouped ────────────────────────────────────────────────
  const summaryGroups = useMemo(() => {
    const grouped = {};
    module9DevPlans.forEach(item => {
      const key = `${item.priorityArea}__${item.responsible}`;
      if (!grouped[key]) {
        grouped[key] = { priorityArea: item.priorityArea, responsible: item.responsible, openPlans: 0, completed: 0, nextReview: "", nearestDeadline: "", executionTypes: new Set() };
      }
      grouped[key].executionTypes.add(item.executionType);
      if (item.status === "Completed") grouped[key].completed++;
      else grouped[key].openPlans++;
      if (item.reviewDate && (!grouped[key].nextReview || item.reviewDate < grouped[key].nextReview)) grouped[key].nextReview = item.reviewDate;
      if (item.deadline && (!grouped[key].nearestDeadline || item.deadline < grouped[key].nearestDeadline)) grouped[key].nearestDeadline = item.deadline;
    });
    return Object.values(grouped);
  }, [module9DevPlans]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const addRow = () => {
    const priorityArea = getFinalPriority();
    const { problemIdentified, rootCause, actionToBeTaken, executionType, responsible, frequency, timeline, successMetric, status, deadline, reviewDate } = form;
    if (!priorityArea || !problemIdentified || !rootCause || !actionToBeTaken) {
      alert("Please fill: Priority Area, Problem Identified, Root Cause, and Action to be Taken."); return;
    }
    if (!executionType || !responsible || !frequency || !timeline || !successMetric || !status) {
      alert("Please fill: Execution Type, Responsible, Frequency, Timeline, Success Metric, and Status."); return;
    }
    setModule9DevPlans(prev => [...prev, { priorityArea, problemIdentified, rootCause, actionToBeTaken, executionType, responsible, frequency, timeline, successMetric, status, deadline, reviewDate }]);
    setForm({ priorityArea: "", customPriority: "", executionType: "", responsible: "", frequency: "", timeline: "", status: "", deadline: "", reviewDate: "", problemIdentified: "", rootCause: "", actionToBeTaken: "", successMetric: "" });
    setActionError("");
  };

  const save = async () => {
    if (!module9DevPlans.length) { alert("Please add at least one student development plan row."); return; }
    setSaving(true);
    setActionError("");
    try {
      const saved = await saveModule9StudentDevelopmentPlan(module9DevPlans);
      setSavedSnapshot(saved || []);
      alert("Student Development Plan saved for current ACPP year.");
    } catch (error) {
      setActionError(error?.message || "Failed to save Module 9 data.");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setSaving(true);
    setActionError("");
    try {
      await loadModule9StudentDevelopmentPlan();
    } catch (error) {
      setActionError(error?.message || "Failed to refresh Module 9 data.");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    setSaving(true);
    setActionError("");
    try {
      await resetModule9StudentDevelopmentPlan();
      setSavedSnapshot([]);
    } catch (error) {
      setActionError(error?.message || "Failed to reset Module 9 data.");
    } finally {
      setSaving(false);
    }
    setForm({ priorityArea: "", customPriority: "", executionType: "", responsible: "", frequency: "", timeline: "", status: "", deadline: "", reviewDate: "", problemIdentified: "", rootCause: "", actionToBeTaken: "", successMetric: "" });
    setFilterPriority(""); setFilterStatus("");
  };

  return (
    <ModuleCard id="module9">
      <ModuleHeader
        step="Module 9"
        title="Student Development Plan"
        description="Create structured student development plans with clear problem definition, root cause identification, actions, accountability, execution rhythm, and measurable outcomes."
        showStatus={false}
      />

      <InfoBanner>
        Capture the priority area, identified problem, root cause, action, responsibility, timeline, and measurable success. <strong>Responsible persons are pulled live from Module 5</strong> staff mappings so leaders can assign ownership to valid teachers and HODs already in the system.
      </InfoBanner>
      {!!(module9LoadError || actionError) && (
        <div className="mt-3 rounded-xl border border-[#fecaca] bg-[#fff1f2] text-[#9f1239] px-3 py-2 text-sm">
          {module9LoadError || actionError}
        </div>
      )}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Development Plan Rows" value={module9DevPlans.length} meta="Total action rows created" />
        <MetricCard label="Priority Areas Used" value={priorityCount} meta="Distinct development priorities" />
        <MetricCard label="Open Plans" value={openCount} meta="Plans still pending / in progress" />
        <MetricCard label="Status" value={moduleStatus} meta={`Completed Plans: ${completedCount}`} />
      </Grid4>

      <SectionDivider />

      {/* Entry Form */}
      {canEditModules1to5 ? (
        <SoftPanel title="Development Plan Entry Form">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <InputLabel>Priority Area</InputLabel>
            <FormSelect value={form.priorityArea} onChange={set("priorityArea")}>
              <option value="">Select priority area</option>
              {PRIORITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Custom Priority Area</InputLabel>
            <FormInput value={form.customPriority} onChange={set("customPriority")} placeholder="Use only if 'Custom' is selected above" />
          </div>
          <div>
            <InputLabel>Execution Type</InputLabel>
            <FormSelect value={form.executionType} onChange={set("executionType")}>
              <option value="">Select execution type</option>
              {EXECUTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Responsible Person</InputLabel>
            <FormSelect value={form.responsible} onChange={set("responsible")}>
              <option value="">Select responsible person</option>
              {staffList.map(n => <option key={n} value={n}>{n}</option>)}
              {!staffList.length && <option value="" disabled>— Add staff in Module 5 first —</option>}
            </FormSelect>
            <HelpText>{staffList.length ? `✓ ${staffList.length} staff member(s) loaded from Module 5.` : "ⓘ No staff found — assign teachers/HODs in Module 5 first."}</HelpText>
          </div>
          <div>
            <InputLabel>Frequency</InputLabel>
            <FormSelect value={form.frequency} onChange={set("frequency")}>
              <option value="">Select frequency</option>
              {FREQUENCY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Timeline</InputLabel>
            <FormInput value={form.timeline} onChange={set("timeline")} placeholder="Example: 2 weeks / Till Term 2 / 30 days" />
          </div>
          <div>
            <InputLabel>Status</InputLabel>
            <FormSelect value={form.status} onChange={set("status")}>
              <option value="">Select status</option>
              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Deadline</InputLabel>
            <FormInput type="date" value={form.deadline} onChange={set("deadline")} />
          </div>
          <div>
            <InputLabel>Next Review Date</InputLabel>
            <FormInput type="date" value={form.reviewDate} onChange={set("reviewDate")} />
          </div>
        </div>

        <SectionDivider />

        <Grid2>
          <div>
            <InputLabel>Problem Identified</InputLabel>
            <FormTextarea value={form.problemIdentified} onChange={set("problemIdentified")} placeholder="What exactly is the issue being observed?" />
          </div>
          <div>
            <InputLabel>Root Cause</InputLabel>
            <FormTextarea value={form.rootCause} onChange={set("rootCause")} placeholder="Why is this issue happening?" />
          </div>
          <div>
            <InputLabel>Action to be Taken</InputLabel>
            <FormTextarea value={form.actionToBeTaken} onChange={set("actionToBeTaken")} placeholder="What intervention or action will be taken?" />
          </div>
          <div>
            <InputLabel>Success Metric</InputLabel>
            <FormTextarea value={form.successMetric} onChange={set("successMetric")} placeholder="How will success be measured?" />
          </div>
        </Grid2>

        <BtnRow>
          <BtnPrimary disabled={!canEditModules1to5 || saving} onClick={addRow}>Add Development Plan Row</BtnPrimary>
          <BtnSecondary disabled={!canEditModules1to5 || saving} onClick={save}>Save Year Data</BtnSecondary>
          <BtnLight disabled={saving} onClick={refresh}>Refresh Year Data</BtnLight>
        </BtnRow>
        </SoftPanel>
      ) : null}

      <SectionDivider />

      {/* Plan Table */}
      <SoftPanel title="Detailed Student Development Plan Table">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">This table shows the full student development plan row by row for review and tracking.</p>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <InputLabel>Filter by Priority Area</InputLabel>
            <FormSelect value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <option value="">All Priority Areas</option>
              {filterPriorities.map(v => <option key={v} value={v}>{v}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Status</InputLabel>
            <FormSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              {filterStatuses.map(v => <option key={v} value={v}>{v}</option>)}
            </FormSelect>
          </div>
        </div>
        <BtnRow>
          <BtnLight onClick={() => { setFilterPriority(""); setFilterStatus(""); }}>Reset Filters</BtnLight>
        </BtnRow>

        <TableWrap>
          <thead>
            <tr>
              <Th>Priority Area</Th><Th>Problem Identified</Th><Th>Root Cause</Th>
              <Th>Action to be Taken</Th><Th>Execution Type</Th><Th>Responsible</Th>
              <Th>Frequency</Th><Th>Timeline</Th><Th>Deadline</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10}><EmptyState>No student development rows match the current filters.</EmptyState></td></tr>
            ) : filtered.map((item, i) => (
              <tr key={i}>
                <Td><TableChip>{item.priorityArea}</TableChip></Td>
                <Td>{item.problemIdentified}</Td><Td>{item.rootCause}</Td>
                <Td>{item.actionToBeTaken}</Td><Td>{item.executionType}</Td>
                <Td>{item.responsible}</Td><Td>{item.frequency}</Td>
                <Td>{item.timeline}</Td><Td>{item.deadline || "—"}</Td>
                <Td><TableChip>{item.status}</TableChip></Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>

      <SectionDivider />

      {/* Leader Summary */}
      <SoftPanel title="Leader Summary View">
        <TableWrap>
          <thead>
            <tr>
              <Th>Priority Area</Th><Th>Responsible</Th><Th>Open Plans</Th>
              <Th>Completed</Th><Th>Next Review</Th><Th>Nearest Deadline</Th><Th>Execution Types</Th>
            </tr>
          </thead>
          <tbody>
            {summaryGroups.length === 0 ? (
              <tr><td colSpan={7}><EmptyState>Leader summary will appear here after development plans are added.</EmptyState></td></tr>
            ) : summaryGroups.map((item, i) => (
              <tr key={i}>
                <Td><TableChip>{item.priorityArea}</TableChip></Td>
                <Td>{item.responsible}</Td>
                <Td>{item.openPlans}</Td>
                <Td>{item.completed}</Td>
                <Td>{item.nextReview || "—"}</Td>
                <Td>{item.nearestDeadline || "—"}</Td>
                <Td>{[...item.executionTypes].map(t => <TableChip key={t}>{t}</TableChip>)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>
    </ModuleCard>
  );
}
